import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Box, 
  Alert, 
  Chip,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { CheckCircle, Schedule, Error, Refresh } from '@mui/icons-material';
import { useKeywordReadiness, FileInfo } from '../../hooks/useKeywordReadiness';

interface KeywordReadinessStatusProps {
  clientId?: string;
  clientName?: string;
  workflowId?: string;
  onStartProcess: () => void;
  isProcessing?: boolean;
  disabled?: boolean;
  refreshTrigger?: number; // Increment this to trigger a refresh after external file uploads
}

const KeywordReadinessStatus: React.FC<KeywordReadinessStatusProps> = ({
  clientId,
  clientName,
  workflowId,
  onStartProcess,
  isProcessing = false,
  disabled = false,
  refreshTrigger
}) => {

  const [persistentFiles, setPersistentFiles] = useState<FileInfo[]>([]);
  const [hasFilesUploaded, setHasFilesUploaded] = useState<boolean | null>(null); // null = unknown, false = no files, true = has files
  const previousFileCountRef = React.useRef<number>(0);
  
  // Use the custom hook for keyword readiness polling - always enabled, we'll control it manually
  const { data: readinessData, isPolling, error, refresh, stopPolling, startPolling } = useKeywordReadiness({
    clientId,
    workflowId,
    enabled: true, // Always enabled, we control start/stop manually
    pollingInterval: 5000, // Poll every 5 seconds
    maxRetries: 10 // Max consecutive network errors before stopping (not for 'not_ready' status)
  });

  // Function to refresh after external file uploads
  const refreshAfterUpload = React.useCallback(() => {
    setHasFilesUploaded(null); // Trigger fresh check for uploaded files
    if (!isPolling) {
      startPolling();
    }
  }, []); // REMOVED deps - isPolling and startPolling are stable!

  // Clear persistent files when client or workflow changes
  React.useEffect(() => {
    setPersistentFiles([]);
    previousFileCountRef.current = 0;
    setHasFilesUploaded(null); // Reset to unknown state to trigger initial check
  }, [clientId, workflowId]);

  // Update persistent files whenever we get new file data
  React.useEffect(() => {
    // Only process if we have actual response data (not initial empty state)
    if (readinessData.status !== 'checking' || readinessData.files !== undefined) {
      if (readinessData.files && readinessData.files.length > 0) {
        const currentFileCount = readinessData.fileCount || readinessData.files.length;
        // Check if files have changed (different count or different files) using ref instead of state
        const filesChanged = previousFileCountRef.current !== currentFileCount;
        
        setPersistentFiles(readinessData.files);
        // Only update hasFilesUploaded if it's not already true (prevent unnecessary re-renders)
        if (hasFilesUploaded !== true) {
          setHasFilesUploaded(true);
        }
        
        // Update ref for next comparison
        previousFileCountRef.current = currentFileCount;
        
        // Log file changes (only when significant)
        if (filesChanged) {
          console.log(`📁 Found ${readinessData.files.length} files for workflow ${workflowId}`);
        }

        // STOP POLLING when files are ready (this was missing!)
        if (readinessData.status === 'ready') {
          console.log(`✅ Files are ready for workflow ${workflowId} - stopping polling`);
          stopPolling();
        }
      } else if (readinessData.fileCount === 0 || (readinessData.files && readinessData.files.length === 0)) {
        // Explicitly received response with 0 files - stop polling
        setPersistentFiles([]);
        // Only update hasFilesUploaded if it's not already false (prevent unnecessary re-renders)
        if (hasFilesUploaded !== false) {
          setHasFilesUploaded(false);
        }
        previousFileCountRef.current = 0;
        console.log(`📭 No files found for workflow ${workflowId} - stopping polling`);
        stopPolling();
      }
    }
  }, [readinessData.files, readinessData.fileCount, readinessData.status, workflowId]); // REMOVED stopPolling - it's stable!

  // Stop polling when processing starts
  React.useEffect(() => {
    if (isProcessing) {
      stopPolling();
    }
  }, [isProcessing]); // REMOVED stopPolling - it's stable!

  // Manual polling control based on file state (prevents infinite loops)
  React.useEffect(() => {
    if (hasFilesUploaded === false) {
      // No files - stop polling
      stopPolling();
    } else if (hasFilesUploaded === null && clientId && workflowId) {
      // Unknown state - start initial check  
      if (!isPolling) {
        startPolling();
      }
    }
    // Don't automatically start polling when hasFilesUploaded becomes true
    // Let the data update effect handle that based on API response
  }, [hasFilesUploaded, clientId, workflowId]); // REMOVED function deps - they're stable!



  // Listen for external file uploads and refresh when refreshTrigger changes
  React.useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refreshAfterUpload();
    }
  }, [refreshTrigger]); // REMOVED refreshAfterUpload - it's stable!

  const getStatusIcon = () => {
    switch (readinessData.status) {
      case 'ready':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'not_ready':
        // Only show circular progress if files are uploaded and being processed
        if (hasFilesUploaded === true) {
          return (
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress 
                sx={{ color: 'warning.main', position: 'absolute' }} 
                size={28} 
                thickness={2}
              />
              <Schedule sx={{ color: 'warning.main', fontSize: 24, zIndex: 1 }} />
            </Box>
          );
        } else {
          // No files uploaded - show static icon
          return <Schedule sx={{ color: 'grey.500' }} />;
        }
      case 'checking':
        return <CircularProgress size={24} />;
      case 'error':
        return <Error sx={{ color: 'error.main' }} />;
      default:
        return <Schedule sx={{ color: 'grey.500' }} />;
    }
  };

  const getStatusColor = () => {
    switch (readinessData.status) {
      case 'ready':
        return 'success';
      case 'not_ready':
        return 'warning';
      case 'checking':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = () => {
    switch (readinessData.status) {
      case 'ready':
        return 'Ready';
      case 'not_ready':
        return 'Not Ready';
      case 'checking':
        return 'Checking...';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const getFileStatusColor = (loadStatus: string) => {
    if (loadStatus === 'LOAD COMPLETED') {
      return 'success';
    } else if (loadStatus.includes('ERROR') || loadStatus.includes('FAILED')) {
      return 'error';
    } else {
      return 'warning';
    }
  };

  const getFileStatusIcon = (loadStatus: string) => {
    if (loadStatus === 'LOAD COMPLETED') {
      return <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />;
    } else if (loadStatus.includes('ERROR') || loadStatus.includes('FAILED')) {
      return <Error sx={{ color: 'error.main', fontSize: 16 }} />;
    } else {
      // Show animated progress ring around the timer icon for processing files
      return (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress 
            sx={{ color: 'warning.main', position: 'absolute' }} 
            size={20} 
            thickness={2}
          />
          <Schedule sx={{ color: 'warning.main', fontSize: 16, zIndex: 1 }} />
        </Box>
      );
    }
  };

  const handleRefresh = () => {
    // Just refresh without clearing persistent files to prevent UI shifting
    // The refresh() function will update the data smoothly
    refresh();
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="h6" fontWeight="bold">
              Keyword Status
            </Typography>
            {clientId && (
              <Tooltip title="Refresh file status">
                <IconButton 
                  onClick={handleRefresh}
                  size="small"
                  disabled={isPolling}
                  sx={{ 
                    color: 'secondary.main',
                    '&:hover': { 
                      backgroundColor: 'secondary.light',
                      color: 'secondary.contrastText' 
                    },
                    '&:disabled': {
                      color: 'action.disabled'
                    }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Upload files to automatically check processing status. Processing can take up to 3 minutes. Click "Load Keywords" when ready.
          </Typography>

          {/* Status Display */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {getStatusIcon()}
              <Chip 
                label={getStatusText()} 
                color={getStatusColor() as any}
                size="small"
              />
            </Box>

            {/* Single status area to prevent UI shifting */}
            <Alert 
              severity={
                hasFilesUploaded === false ? 'info' :
                readinessData.status === 'error' ? 'error' :
                readinessData.status === 'ready' ? 'success' : 'info'
              } 
              icon={false}
              sx={{ mb: 2, minHeight: '80px' }}
            >
              {hasFilesUploaded === false ? (
                <Typography variant="body2">
                  <strong>No files uploaded yet.</strong> Upload your keyword files first using the upload button in the Quick Start section.
                </Typography>
              ) : persistentFiles && persistentFiles.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Files ({persistentFiles.length}):
                  </Typography>
                  {persistentFiles.map((file, index) => (
                    <Box key={index} sx={{ mb: index < persistentFiles.length - 1 ? 1.5 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {getFileStatusIcon(file.load_status)}
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          sx={{ 
                            wordBreak: 'break-word',
                            flexGrow: 1
                          }}
                        >
                          {file.file_name || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {file.created_at ? new Date(file.created_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption">•</Typography>
                        <Chip
                          label={file.load_status || 'Unknown'}
                          color={getFileStatusColor(file.load_status) as any}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : hasFilesUploaded === null ? (
                <Typography variant="body2">
                  Checking for uploaded files...
                </Typography>
              ) : (
                <Typography variant="body2">
                  {readinessData.message || 'Checking file status...'}
                </Typography>
              )}
            </Alert>

            {/* Keywords count - always present to prevent shifting */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: '20px' }}>
              {typeof readinessData.keywordCount === 'number' 
                ? `Keywords available: ${readinessData.keywordCount.toLocaleString()}` 
                : '\u00A0' /* Non-breaking space to maintain height */
              }
            </Typography>

            {/* Last updated - always present to prevent shifting */}
            <Typography variant="caption" color="text.secondary" sx={{ minHeight: '18px' }}>
              {readinessData.lastUpdated 
                ? `Last checked: ${new Date(readinessData.lastUpdated).toLocaleTimeString()}`
                : '\u00A0' /* Non-breaking space to maintain height */
              }
            </Typography>
          </Box>

          {/* Action Button - single button to prevent shifting */}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Button
              variant={hasFilesUploaded === true && readinessData.status === 'ready' ? "contained" : "outlined"}
              color="primary"
              onClick={hasFilesUploaded === true ? onStartProcess : undefined}
              disabled={
                disabled || 
                isProcessing || 
                hasFilesUploaded !== true ||
                (readinessData.status !== 'ready' && readinessData.status !== 'checking') ||
                isPolling
              }
              fullWidth
              sx={{
                minHeight: '40px',
                height: '40px', // Fixed height to prevent size changes
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {isProcessing ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading Keywords...
                </>
              ) : hasFilesUploaded === false ? (
                'Upload Files First'
              ) : hasFilesUploaded === null ? (
                'Checking for Files...'
              ) : readinessData.status === 'ready' ? (
                'Load Keywords'
              ) : (
                'Load Keywords (When Ready)'
              )}
            </Button>
          </Box>

          {/* How it works section */}
          <Box mt={4}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              How it works
            </Typography>
            <Box component="ol" sx={{ 
              pl: 2, 
              fontSize: '0.875rem', 
              color: 'text.secondary',
              '& > li': { mb: 1 }
            }}>
              <li>Upload your keyword files first</li>
              <li>System automatically checks if files are processed</li>
              <li>Click "Load Keywords" button when files are ready</li>
              <li>Keywords will be available for analysis in blob storage</li>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default KeywordReadinessStatus; 